import { startTransition, useCallback } from 'react'
import { usePriceIntentAttribution } from '@/features/recommendations/usePriceIntentAttribution'
import { useShowAppError } from '@/services/appErrors/appErrorAtom'
import {
  PriceIntentConfirmMutationVariables,
  PriceIntentFragment,
  usePriceIntentConfirmMutation,
} from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { usePriceCalculatorStep } from '../../usePriceCalculatorStep'

export const useConfirmPriceIntent = () => {
  const { getPriceIntentAttributionField, clearAfterSuccessfulConfirm } = usePriceIntentAttribution()

  const showError = useShowAppError()
  const { setStep } = usePriceCalculatorStep()
  const [confirmPriceIntent] = usePriceIntentConfirmMutation({
    onError(error, options) {
      const { priceIntentId } = (options?.variables ?? {}) as PriceIntentConfirmMutationVariables
      logger.warn('Failed to confirm price intent', {
        error,
        priceIntentId,
      })
      showError(error)
      // Prevent form flicker until dialog opens
      startTransition(() => setStep('fillForm'))
    },
    onCompleted(data, options) {
      const { priceIntentId } = (options?.variables ?? {}) as PriceIntentConfirmMutationVariables
      const updatedPriceIntent = data.priceIntentConfirm.priceIntent
      if (updatedPriceIntent) {
        clearAfterSuccessfulConfirm(updatedPriceIntent.product.name)
        setStep('viewOffers')
      } else {
        throw new Error(`UNEXPECTED: price intent not updated without user error (${priceIntentId})`)
      }
    },
  })

  return useCallback(
    (priceIntent: PriceIntentFragment) => {
      setStep('calculatingPrice')
      confirmPriceIntent({
        variables: {
          priceIntentId: priceIntent.id,
          generateRecommendations: true,
          ...getPriceIntentAttributionField(priceIntent.product.name),
        },
      })
    },
    [confirmPriceIntent, getPriceIntentAttributionField, setStep],
  )
}
